import React from 'react';
import { HeartIcon, ShieldIcon, StarIcon, TrophyIcon } from 'lucide-react';
import { Role } from '../types/commission';
interface ExecutiveTierCardProps {
  role: Role;
  tiers: Role[];
}
const confetti = [{
  className: 'left-2 top-6 rotate-12 bg-[#e8b923]'
}, {
  className: 'right-4 top-2 -rotate-12 bg-brand-500'
}, {
  className: 'bottom-10 left-0 rotate-45 bg-brand-300'
}, {
  className: 'bottom-4 right-2 -rotate-6 bg-[#f5d061]'
}];

/** Celebration card shown once a producer reaches the top production tier. */
export function ExecutiveTierCard({
  role,
  tiers
}: ExecutiveTierCardProps) {
  return <article aria-labelledby="executive-tier-heading" className="relative h-full overflow-hidden rounded-xl border border-[#e8b923]/60 bg-[#fffdf5] p-6 shadow-card" data-id="element-250" data-mp="1188py9jvzqlc">
      <div className="flex h-full flex-col gap-6 sm:flex-row sm:items-center" data-id="element-251" data-mp="2vlgrek2db0fy">
        <div className="flex min-w-0 flex-1 flex-col" data-id="element-252" data-mp="2qmdkeu2tuk5c">
          <div className="flex items-start justify-between gap-4" data-id="element-253" data-mp="2qmnfgw2qbhhw">
            <div data-id="element-254" data-mp="2n5l4ltzvuj3s">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#b8860b]" data-id="element-255" data-mp="2n51q6n9fycui">Current tier</p>
              <h2 id="executive-tier-heading" className="mt-1.5 text-2xl font-bold text-slate-900" data-id="element-256" data-mp="2s6in67ot1f5t">
                {role.name}
              </h2>
              <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#e8b923]/15 px-2.5 py-1 text-xs font-bold text-[#8a6508]" data-id="element-257" data-mp="2i6hz68fzqava">
                <ShieldIcon className="h-3.5 w-3.5 fill-[#e8b923] text-[#b8860b]" aria-hidden="true" data-id="element-258" data-mp="35wui0ecrxs0n" />
                {role.baseBps} BPS base
              </span>
            </div>
            <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e8b923]/15 ring-1 ring-[#e8b923]/40 sm:hidden" data-id="element-259" data-mp="2w5hq5mqz71um">
              <StarIcon className="h-5 w-5 fill-[#e8b923] text-[#e8b923]" data-id="element-260" data-mp="3oueusn29l04w" />
            </span>
          </div>

          <div className="mt-5 flex gap-1.5" aria-hidden="true" data-id="element-261" data-mp="3tnhs96a5wtqd">
            {tiers.map(tier => <span key={tier.id} className="h-1.5 flex-1 rounded-full bg-[#e8b923]" data-id="element-262" data-mp="7bzllgywhfrp" />)}
          </div>
          <p className="mt-2 text-xs font-bold uppercase tracking-wide text-[#b8860b]" data-id="element-263" data-mp="2bbtst2ukuwf">Top tier reached!</p>

          <div className="mt-5 flex items-start gap-3 rounded-lg bg-white px-3.5 py-3 ring-1 ring-[#e8b923]/30" data-id="element-264" data-mp="3uj1wsjbzr2sr">
            <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#e8b923]/15 text-[#b8860b]" data-id="element-265" data-mp="3kj1d2wa7mko1">
              <TrophyIcon className="h-4 w-4" data-id="element-266" data-mp="3fqhutm0kv1cw" />
            </span>
            <div data-id="element-267" data-mp="3atrh35c8whun">
              <p className="text-sm font-bold text-slate-900" data-id="element-268" data-mp="3ar5bvcctuefl">Congratulations!</p>
              <p className="text-sm text-slate-600" data-id="element-269" data-mp="36t7ngbqvrwd9">You’ve reached the highest level of success.</p>
            </div>
          </div>

          <div aria-hidden="true" className="my-4 h-px bg-[#e8b923]/30" data-id="element-270" data-mp="31ue9yjblg5d3" />
          <p className="text-sm text-slate-600" data-id="element-271" data-mp="2wu8l1awpkwyp">Thank you for leading the way and setting the standard.</p>
          <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900" data-id="element-272" data-mp="1cdingifjvfiq">
            Keep inspiring excellence!
            <HeartIcon className="h-4 w-4 fill-red-500 text-red-500" aria-hidden="true" data-id="element-273" data-mp="1m92i71ido85w" />
          </p>
        </div>

        <div aria-hidden="true" className="relative mx-auto hidden w-36 shrink-0 sm:block" data-id="element-274" data-mp="1qa5vbg91jq3f">
          <div className="absolute inset-0 rounded-full bg-[#e8b923]/10" data-id="element-275" data-mp="tlpvmrc7w0cl" />
          {confetti.map(piece => <span key={piece.className} className={`absolute h-2 w-1 rounded-sm ${piece.className}`} data-id="element-276" data-mp="ykt0aaoac4k1" />)}
          <ExecutiveEmblem data-id="element-277" data-mp="12k1vljyz4ahp" />
        </div>
      </div>
    </article>;
}
function ExecutiveEmblem() {
  return <svg className="relative w-full" viewBox="0 0 220 260" focusable="false" data-id="element-278" data-mp="17efrrolgmlnf">
      <defs data-id="element-279" data-mp="atxblmf4lph0">
        <linearGradient id="execGold" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-280" data-mp="au6temq0oqbm">
          <stop offset="0%" stopColor="#f9e076" data-id="element-281" data-mp="eupkufnzzuda" />
          <stop offset="45%" stopColor="#e8b923" data-id="element-282" data-mp="3rwt54lwafhik" />
          <stop offset="100%" stopColor="#b8860b" data-id="element-283" data-mp="3rxl1w01mflaf" />
        </linearGradient>
        <linearGradient id="execGoldDark" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-284" data-mp="3myhvoc9wn90v">
          <stop offset="0%" stopColor="#f5d061" data-id="element-285" data-mp="5gynqrloyym1" />
          <stop offset="100%" stopColor="#c9971a" data-id="element-286" data-mp="5irtm4ohdesz" />
        </linearGradient>
      </defs>
      <ellipse cx="110" cy="118" rx="92" ry="92" fill="none" stroke="url(#execGold)" strokeWidth="4" data-id="element-287" data-mp="pqd5s8ml77a" />
      <ellipse cx="110" cy="118" rx="78" ry="78" fill="#fffdf5" stroke="url(#execGoldDark)" strokeWidth="3" data-id="element-288" data-mp="eglnl8yrvwcz" />
      <path d="M78 52h64l-8 18H86l-8-18Z" fill="url(#execGold)" data-id="element-289" data-mp="9iaitcnc4c1m" />
      <path d="M86 70h48v8c0 8-10 14-24 14s-24-6-24-14v-8Z" fill="url(#execGoldDark)" data-id="element-290" data-mp="ogcn5p26x1jx" />
      <circle cx="110" cy="44" r="5" fill="url(#execGold)" data-id="element-291" data-mp="jgghzmhrb42s" />
      <text x="110" y="108" textAnchor="middle" fontSize="18" fontWeight="700" fill="#0f172a" data-id="element-292" data-mp="jewx1il5o4uo">
        EXECUTIVE
      </text>
      <text x="110" y="128" textAnchor="middle" fontSize="11" fontWeight="700" fill="#b8860b" letterSpacing="2" data-id="element-293" data-mp="2l8mgsbxl46zk">
        LOAN OFFICER
      </text>
      <g fill="url(#execGold)" data-id="element-294" data-mp="10qu56c47chl3">
        <path d="M82 142 84 148 90 148 85 152 87 158 82 154 77 158 79 152 74 148 80 148Z" data-id="element-295" data-mp="10sofyxlretbg" />
        <path d="M98 146 100 152 106 152 101 156 103 162 98 158 93 162 95 156 90 152 96 152Z" data-id="element-296" data-mp="15rhwz00992gd" />
        <path d="M110 144 112 150 118 150 113 154 115 160 110 156 105 160 107 154 102 150 108 150Z" data-id="element-297" data-mp="qyelnad9bky3" />
        <path d="M122 146 124 152 130 152 125 156 127 162 122 158 117 162 119 156 114 152 120 152Z" data-id="element-298" data-mp="vxixy9vh9ysn" />
        <path d="M138 142 140 148 146 148 141 152 143 158 138 154 133 158 135 152 130 148 136 148Z" data-id="element-299" data-mp="i270vvtunw8k" />
      </g>
      <path d="M44 118c18-28 38-42 66-42s48 14 66 42" fill="none" stroke="url(#execGold)" strokeWidth="5" strokeLinecap="round" data-id="element-300" data-mp="81mw4j74g9ll" />
      <path d="M52 188c20 16 44 24 58 24s38-8 58-24" fill="url(#execGold)" data-id="element-301" data-mp="czy6cv1bwm4c" />
      <path d="M62 196h96c-8 10-24 16-48 16s-40-6-48-16Z" fill="url(#execGoldDark)" data-id="element-302" data-mp="22fxkyrnpv3rd" />
      <text x="110" y="214" textAnchor="middle" fontSize="13" fontWeight="700" fill="#0f172a" letterSpacing="1" data-id="element-303" data-mp="22dut023070k0">
        BEST TIER
      </text>
    </svg>;
}